import {Color, Euler, Matrix4, Mesh, ShaderMaterial, Vector2, Vector3, Vector4, WebGLRenderer} from 'three'

import {bgColor, canvasResSize} from '../../_config'
import {BREAKPOINT} from '../../_rawConfig'
import fragmentShader from '../../_shaders/cube/cube'
import fragmentShaderForGlass from '../../_shaders/cube/glassCube'
import fragmentShaderForGlassNoBlur from '../../_shaders/cube/glassNoBlurCube'
import fragmentShaderMosaicCube from '../../_shaders/cube/mosaicCube'
import fragmenShaderRainbowCube from '../../_shaders/cube/rainbowCube'
import fragmentShaderWhiteBlueCube from '../../_shaders/cube/whiteBlueCube'
import fragmentShaderWhiteGreenCube from '../../_shaders/cube/whiteGreenCube'
import vertexShader from '../../_shaders/vertexShader'
import type GlassEffect from '../../brandedDivider/GlassEffect'
import type {CubeConfig} from './_types'
import type RoundedCubeGeometry from './RoundedCubeGeometry'

interface SingleCubeProps {
  cubeConfig: CubeConfig
  cubeGeometry: RoundedCubeGeometry
  glassEffect?: GlassEffect
  rectData: Vector4
  _rectData: Vector4
}

export default class SingleCube {
  mesh: Mesh
  cubeGeometry: RoundedCubeGeometry
  glassEffect?: GlassEffect
  cubeConfig: CubeConfig
  progress: Vector4 = new Vector4(0, 0, 0, 0)
  commonUniforms: {
    uInnerShadowColor: {value: Color}
    uBgColor: {value: Color}
    uPixelRatio: {value: number}
    uLightPos: {value: Vector3}
    uRectData: {value: Vector4}
    uRectData2: {value: Vector4}
    uResolution: {value: Vector2}
  } = {
    uInnerShadowColor: {value: new Color(0x5fed83)},
    uBgColor: {value: bgColor},
    uPixelRatio: {value: 1},
    uLightPos: {value: new Vector3(2.0, 2.0, 1.0).normalize()},
    uRectData: {value: new Vector4()},
    uRectData2: {value: new Vector4()},
    uResolution: {value: canvasResSize},
  }
  constructor(props: SingleCubeProps) {
    this.cubeGeometry = props.cubeGeometry
    this.glassEffect = props.glassEffect
    this.cubeConfig = props.cubeConfig

    this.commonUniforms.uRectData.value = props.rectData
    this.commonUniforms.uRectData2.value = props._rectData

    this.mesh = this.createCubeMesh(this.cubeConfig.cubeType)

    this.mesh.userData.position = {
      def: this.cubeConfig.position.clone(),
      offset: this.cubeConfig.offsetStart.position.clone(),
      current: new Vector3(0, 0, 0),
    }

    const scale = this.cubeConfig.scale

    if (scale instanceof Vector3) {
      this.mesh.scale.copy(scale)
    } else {
      this.mesh.scale.set(scale, scale, scale)
    }

    this.mesh.userData.rotation = {
      def: this.cubeConfig.rotation.clone(),
      offset: this.cubeConfig.offsetStart.rotation.clone(),
      current: new Vector3(0, 0, 0),
      defMatrix: new Matrix4(),
      offsetMatrix: new Matrix4(),
      currentMatrix: new Matrix4(),
      defEuler: new Euler(),
      offsetEuler: new Euler(),
      scrollDiffEuler: new Euler(0, 0, 0),
      scrollDiffMatrix: new Matrix4(),
    }

    this.mesh.visible = false
  }

  createCubeMesh(cubeType: string) {
    let material: ShaderMaterial
    const random = new Vector4(Math.random(), Math.random(), Math.random(), Math.random())

    switch (cubeType) {
      case 'normal':
      case 'normal2':
      default:
        material = new ShaderMaterial({
          vertexShader,
          fragmentShader,
          uniforms: {
            ...this.commonUniforms,
            uColor1: {value: new Color(0x028e29)}, // Dark Green
            uColor2: {value: new Color(0xbfffd1)}, // Light Green
            uColor3: {value: new Color(0x21a545)}, // Light Green2

            uProgress: {value: this.progress},
            uRandom: {value: random},
          },
        })

        if (cubeType === 'normal2') {
          material.defines.NORMAL2 = ''
        }
        break
      case 'mosaic':
        material = new ShaderMaterial({
          vertexShader,
          fragmentShader: fragmentShaderMosaicCube,
          uniforms: {
            ...this.commonUniforms,
            uColor1: {
              value: new Color(0x31d160),
            },
            uColor2: {value: new Color(0xbfffd1)},
            uProgress: {value: this.progress},
            uRandom: {value: random},
          },
        })
        break
      case 'glass':
        material = new ShaderMaterial({
          vertexShader,
          fragmentShader: fragmentShaderForGlass,
          uniforms: {
            ...this.commonUniforms,
            uColor1: {value: new Color(0x028e29)}, // Dark Green
            uColor2: {value: new Color(0xbfffd1)}, // Light Green

            uFboSize: {value: this.glassEffect?.fboSize},
            tGlass: {value: this.glassEffect?.fbo1.texture},
            uProgress: {value: this.progress},
            uRandom: {value: random},
          },
        })
        break
      case 'glassNoBlur':
      case 'glassRainbowNoBlur':
      case 'glassMosaic':
        material = new ShaderMaterial({
          vertexShader,
          fragmentShader: fragmentShaderForGlassNoBlur,
          uniforms: {
            ...this.commonUniforms,
            uColor1: {value: new Color(cubeType === 'glassRainbowNoBlur' ? 0xdef6ff : 0xc9f3ff)}, // Dark Green
            uColor2: {value: new Color(0xbfffd1)}, // Light Green

            uFboSize: {value: this.glassEffect?.fboSize},
            tGlass: {value: this.glassEffect?.fbo1.texture},
            uProgress: {value: this.progress},
            uRandom: {value: random},
          },
          transparent: true,
        })

        if (cubeType === 'glassMosaic') {
          material.defines.MOSAIC = ''
        }

        if (cubeType === 'glassRainbowNoBlur') {
          material.defines.RAINBOW = ''
        }
        break
      case 'whiteGreen':
        material = new ShaderMaterial({
          vertexShader,
          fragmentShader: fragmentShaderWhiteGreenCube,
          uniforms: {
            ...this.commonUniforms,
            uInnerShadowColor2: {value: new Color(0x1cdcff)},
            uColor1: {value: new Color(0x56cf97)}, // Dark Green
            uColor2: {value: new Color(0xf2fdff)}, // white
            uColor3: {value: new Color(0xa9f1c3)}, // Light Green

            uProgress: {value: this.progress},
            uRandom: {value: random},
          },
        })
        break
      case 'whiteBlue':
        material = new ShaderMaterial({
          vertexShader,
          fragmentShader: fragmentShaderWhiteBlueCube,
          uniforms: {
            ...this.commonUniforms,
            uInnerShadowColor2: {value: new Color(0x1cdcff)},
            uColor1: {value: new Color(0xa7d8f5)}, // Dark blue
            uColor2: {value: new Color(0xf2fdff)}, // white
            uColor3: {value: new Color(0xd4f6f7)}, // Light Green

            uProgress: {value: this.progress},
            uRandom: {value: random},
          },
        })
        break
      case 'rainbow':
        material = new ShaderMaterial({
          vertexShader,
          fragmentShader: fragmenShaderRainbowCube,
          uniforms: {
            ...this.commonUniforms,
            uColor1: {
              value: new Color(0xdef6ff),
            },
            uColor2: {
              value: new Color(0xffdeed),
            },
            uColor3: {
              value: new Color(0xfffdde),
            },
            uProgress: {value: this.progress},
            uRandom: {value: random},
          },
        })
        break
    }

    const cube = new Mesh(this.cubeGeometry, material)

    return cube
  }

  createAnimation(tl: gsap.core.Timeline) {
    const delay = this.cubeConfig.delay
    const duration = this.cubeConfig.duration

    tl.add(() => {
      this.mesh.visible = true
    }, delay)
      .to(
        this.mesh.userData.position.offset,
        {
          x: 0,
          y: 0,
          z: 0,
          ease: 'expo.out',
          duration,
        },
        delay,
      )
      .to(
        this.mesh.userData.rotation.offset,
        {
          x: 0,
          y: 0,
          z: 0,
          ease: 'expo.out',
          duration,
        },
        delay,
      )
      .to(
        this.progress,
        {
          x: 1,
          ease: 'expo.out',
          duration: 0.8,
        },
        delay,
      )
      .to(
        this.progress,
        {
          y: 1,
          duration: 1.5,
        },
        delay,
      )
      .to(
        this.progress,
        {
          z: 1,
          ease: 'steps(8)',
          duration: 1.8,
        },
        delay,
      )
  }

  update(renderer: WebGLRenderer) {
    this.commonUniforms.uPixelRatio.value = renderer.getPixelRatio()
    const position = this.mesh.userData.position
    const rotation = this.mesh.userData.rotation

    position.current.copy(position.def).add(position.offset)
    this.mesh.position.copy(position.current)
    const layout = this.cubeConfig.layout

    this.mesh.visible = true

    if (window.innerWidth < BREAKPOINT.MOBILE) {
      this.mesh.visible = layout.mobile.visible
      this.mesh.position.add(layout.mobile.offset)
    } else if (window.innerWidth < BREAKPOINT.TABLET) {
      this.mesh.visible = layout.tablet.visible
      this.mesh.position.add(layout.tablet.offset)
    }

    // Apply rotations using matrix composition: rotation.def * rotation.offset
    rotation.defEuler.set(rotation.def.x, rotation.def.y, rotation.def.z, 'XYZ')
    rotation.offsetEuler.set(rotation.offset.x, rotation.offset.y, rotation.offset.z, 'XYZ')
    rotation.defMatrix.makeRotationFromEuler(rotation.defEuler)
    rotation.offsetMatrix.makeRotationFromEuler(rotation.offsetEuler)

    rotation.scrollDiffEuler.x += 0.01
    rotation.scrollDiffMatrix.makeRotationFromEuler(rotation.scrollDiffEuler)

    rotation.currentMatrix.copy(rotation.offsetMatrix)
    rotation.currentMatrix.multiply(rotation.defMatrix)

    // Extract the final rotation back to Euler and apply to mesh
    this.mesh.rotation.setFromRotationMatrix(rotation.currentMatrix)
  }
}
